import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Typography,
  Tooltip,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CustomDropdown from "../SizesColorsDropdown";
import CustomTextField from "../../CustomTextField";
import AddVariationsFlyout from "./AddVariationsFlyout";
import PricingSettingsPopover from "./PricingSettingsPopover";
import styles from "./index.module.css";
import PricingTypeSelect from "../PricingTypeDropdown";
import {
  DISPLAY_STATUS,
  DISPLAY_STATUS_OPTIONS,
  normalizeDisplayStatus,
} from "../../../utils/pricingOptions";
import { computePromoStorePricingBreakdownRows } from "../../../utils/pricingEngine";
import {
  formatMinimumTwoDecimalPlaces,
  normalizeEditableDecimalForCommit,
} from "../pricingDisplay";


import { Close as CloseIcon } from "@mui/icons-material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

// allow: "", "42", "42.", ".5", "0.75"
const DECIMAL_RX = /^(\d+(\.\d*)?|\.\d*)?$/;
// allow only digits (for qty)
const INTEGER_RX = /^\d*$/;

const makeTempRowId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalizeVariationValue = (value) => String(value ?? "").trim();
const normalizeVariationKey = (value) => normalizeVariationValue(value).toLowerCase();
const normalizeSkuValue = (value) => String(value ?? "").trim();
const getRowVisualKey = (row, index) => String(row?.id || row?.tempRowId || index);
const getStableRowKey = (row) => {
  if (row?.id != null && row?.id !== "") return `id:${row.id}`;
  if (row?.tempRowId) return `temp:${row.tempRowId}`;
  return null;
};
const getPromoStoreSnapshotRowKey = (row) => {
  if (row?.id != null && row?.id !== "") return String(row.id);
  if (row?.tempRowId) return String(row.tempRowId);
  return null;
};
const buildVariationSkuMatchKey = (color, size) =>
  `${normalizeVariationKey(color)}::${normalizeVariationKey(size)}`;

const toStrings = (opts = []) =>
  (opts || [])
    .map((o) =>
      typeof o === "string" ? o : o?.name ?? o?.label ?? o?.id ?? ""
    )
    .filter(Boolean);

const isLockedBool = (v) => v === true || String(v).toLowerCase() === "true";

const isDefaultNumber = (value, expected) => Number(value) === Number(expected);
const isEmptyConvertedPricingValue = (value) =>
  value === null || value === undefined || value === "" || Number(value) === 0;

const isEmptyValue = (value) => value === null || value === undefined || value === "";

// This is deliberately an allowlist. Pricing rows can also carry persisted IDs and
// cart/snapshot relationship keys; none of those are part of a visible line and
// must ever move from the source row to a target row.
export const BREAKDOWN_ROW_CLONE_FIELDS = [
  "priceType",
  "color",
  "size",
  "sku",
  "quantity",
  "netCost",
  "margin",
  "retail",
  "clientPrice",
  "total",
  "inventoryTracking",
  "pricingOption",
  "internationalPricingOption",
  "convertedNetCost",
  "ConvertedNetCost",
  "exchangeRate",
  "ExchangeRate",
  "isManual",
  "isLocked",
  "isRetailLocked",
];

export const isUntouchedBreakdownRow = (row = {}, defaultMargin = 40) =>
  row?.priceType === "Breakdown" &&
  isEmptyValue(row?.color) &&
  isEmptyValue(row?.size) &&
  isEmptyValue(row?.sku) &&
  isDefaultNumber(row?.quantity, 1) &&
  isDefaultNumber(row?.netCost, 0) &&
  isDefaultNumber(row?.margin, defaultMargin) &&
  isDefaultNumber(row?.retail, 0) &&
  isDefaultNumber(row?.clientPrice, 0) &&
  isDefaultNumber(row?.total, 0) &&
  (isEmptyValue(row?.inventoryTracking) || row?.inventoryTracking === "Track inventory") &&
  (isEmptyValue(row?.pricingOption) || row?.pricingOption === "Display to Client") &&
  (isEmptyValue(row?.internationalPricingOption) || row?.internationalPricingOption === "Display to Client") &&
  !isLockedBool(row?.isLocked) &&
  !isLockedBool(row?.isRetailLocked) &&
  row?.isManual !== true && String(row?.isManual || "").toLowerCase() !== "true" &&
  isEmptyConvertedPricingValue(row?.convertedNetCost ?? row?.ConvertedNetCost) &&
  isEmptyConvertedPricingValue(row?.exchangeRate ?? row?.ExchangeRate);

export const buildFirstRowClonePatch = (source = {}) => {
  const patch = {};
  BREAKDOWN_ROW_CLONE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(source, field)) patch[field] = source[field];
  });

  // Both casing variants occur during edit/hydration. Keep them synchronized so
  // the pricing calculator and save serializer see the same currency context.
  if (Object.prototype.hasOwnProperty.call(source, "convertedNetCost") || Object.prototype.hasOwnProperty.call(source, "ConvertedNetCost")) {
    patch.convertedNetCost = source.convertedNetCost ?? source.ConvertedNetCost;
    patch.ConvertedNetCost = patch.convertedNetCost;
  }
  if (Object.prototype.hasOwnProperty.call(source, "exchangeRate") || Object.prototype.hasOwnProperty.call(source, "ExchangeRate")) {
    patch.exchangeRate = source.exchangeRate ?? source.ExchangeRate;
    patch.ExchangeRate = patch.exchangeRate;
  }
  return patch;
};

export const applyFirstRowToUntouchedBreakdownRows = (
  rows = [],
  { defaultMargin = 40, applyPricingPatch, canApply = () => true } = {}
) => {
  if (typeof applyPricingPatch !== "function") return rows;
  const indexes = rows.reduce((result, row, index) => {
    if (row?.priceType === "Breakdown") result.push(index);
    return result;
  }, []);
  const sourceIndex = indexes[0];
  const source = rows[sourceIndex];
  if (!source) return rows;
  const patch = buildFirstRowClonePatch(source);
  const unlockedPatch = { ...patch };
  delete unlockedPatch.isLocked;
  let changed = false;
  const nextRows = rows.map((row, index) => {
    if (index === sourceIndex || !indexes.includes(index)) return row;
    if (!isUntouchedBreakdownRow(row, defaultMargin) || !canApply(row, patch)) return row;
    changed = true;
    // Run through the established calculator first, then apply the source's
    // already-normalized visible values verbatim. isLocked is applied last so a
    // locked source cannot make the calculator freeze the target's defaults.
    const normalized = applyPricingPatch(row, unlockedPatch);
    return { ...normalized, ...patch };
  });
  return changed ? nextRows : rows;
};

const hasConvertedCostBasis = (row = {}) => {
  const converted = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  return Number.isFinite(converted) && converted > 0 && Number.isFinite(rate) && rate > 0;
};
const getPromoStoreRowUnitCost = (row = {}) => {
  const converted = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  if (Number.isFinite(converted) && converted > 0) return converted;
  const net = Number(row.netCost ?? row.NetCost ?? row.Cost ?? row.cost);
  return Number.isFinite(net) ? net : 0;
};

const INVENTORY_TRACKING = {
  track: "Track inventory",
  noTrack: "Do not track inventory",
};

export const resolveInventoryTracking = (value) =>
  value === INVENTORY_TRACKING.noTrack ? INVENTORY_TRACKING.noTrack : INVENTORY_TRACKING.track;

export const areAllPricingRowsTrackingInventory = (rows = []) =>
  (rows || []).length > 0 &&
  (rows || []).every((row) => resolveInventoryTracking(row?.inventoryTracking) === INVENTORY_TRACKING.track);

export const bulkTogglePricingRowsInventoryTracking = (rows = []) => {
  const nextInventoryTracking = areAllPricingRowsTrackingInventory(rows)
    ? INVENTORY_TRACKING.noTrack
    : INVENTORY_TRACKING.track;

  return (rows || []).map((row) => ({
    ...row,
    inventoryTracking: nextInventoryTracking,
  }));
};

const formatPromoStoreMoney = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const formatPromoStorePercent = (value) => `${(Number(value) || 0).toFixed(2)}%`;
const promoStoreRetailHeaderTooltip =
  "Independent storefront price. Does not affect Client Price or Total.";
const promoStoreClientPriceHeaderTooltip =
  "Calculated unit price using Net Cost + decoration costs, then applying Margin %.";

// This pricing-mode action is available only to Super Administrators.
export const getManualPricingModeTooltip = (row = {}) =>
  `REMOVE, Only super administrator - ${row?.isManual
    ? "Manual pricing: retail does not auto-calc from margin"
    : "Auto pricing: retail derives from cost & margin"}`;

// consider transient only when coercing to Number would be lossy
const isTransientDecimal = (v) => {
  if (typeof v !== "string") return false;
  const s = v;
  return (s === "" ||
  s === "." ||
  /^\d+\.$/.test(s) || /^\d+\.0+$/.test(s));
};

const PricingBreakDown = ({
  rows,
  setRows,
  colorOptions,
  setColorOptions,
  setSizeOptions,
  orderItem,
  sortRows,
  sizeOptions,
  // from parent
  patchRow,     // (index, patch)
  toggleLock,   // (index)
  showRetailBulkLock = false,
  retailBulkLocked = false,
  onToggleRetailBulkLock = null,
  lockConfig,   // { isFieldDisabled: (row, field) => boolean }
  respectRowLocks = true,
  skuOptions = [],
  variationOptions = [],
  inventoryMode = false,
  defaultMargin = 40,
  supplierCurrencyCode = "",
  pricingCurrencyCode = "",
  showNetCostLocks = false,
  bulkNetCostLocked = false,
  onToggleBulkNetCostLock = null,
  pricingGridMode = false,
  onPricingGridModeChange = null,
  allowManualPricing = true,
  canSeeManualPricingToggle = false,
  decorations = [],
  applyPricingPatch,
  separateInventoryFromPricing = false,
}) => {
  const costDropdownRef = useRef(null);
  const costDropdownMenuRef = useRef(null);
  const intlCostDropdownRef = useRef(null);
  const intlCostDropdownMenuRef = useRef(null);
  const variationsAnchorRef = useRef(null);
  const [showCostDropdown, setShowCostDropdown] = useState(false);
  const [showInternationalCostDropdown, setInternationalShowCostDropdown] =
    useState(false);
  const [showVariationsFlyout, setShowVariationsFlyout] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [autoMatchedSkuRows, setAutoMatchedSkuRows] = useState({});
  const [pendingDeleteRowKey, setPendingDeleteRowKey] = useState(null);
  const [showInventoryBulkAction, setShowInventoryBulkAction] = useState(false);
  const autoMatchTimersRef = useRef({});
  const canToggleManualPricing = allowManualPricing && canSeeManualPricingToggle;

  const breakdownIndexes = useMemo(() =>
    (rows || []).reduce((indexes, row, index) => {
      if (row?.priceType === "Breakdown") indexes.push(index);
      return indexes;
    }, []), [rows]);
  const firstBreakdownIndex = breakdownIndexes[0] ?? -1;
  const hasUntouchedBreakdownBelow = breakdownIndexes
    .slice(1)
    .some((index) => isUntouchedBreakdownRow(rows[index], defaultMargin));

  const variationSkuResolver = useMemo(() => {
    const matchMap = new Map();

    (variationOptions || []).forEach((variation) => {
      const color = normalizeVariationValue(variation?.color);
      const size = normalizeVariationValue(variation?.size);
      const sku = normalizeSkuValue(variation?.sku);
      if (!color || !size || !sku) return;

      const key = buildVariationSkuMatchKey(color, size);
      const existing = matchMap.get(key) || {
        color,
        size,
        skus: new Set(),
      };
      existing.skus.add(sku);
      matchMap.set(key, existing);
    });

    return {
      resolve(color, size) {
        const key = buildVariationSkuMatchKey(color, size);
        const entry = matchMap.get(key);
        if (!entry) return { status: "no-match", sku: "" };

        const distinctSkus = Array.from(entry.skus).filter(Boolean);
        if (distinctSkus.length === 1) {
          return { status: "match", sku: distinctSkus[0] };
        }
        if (distinctSkus.length > 1) {
          return { status: "ambiguous", sku: "" };
        }
        return { status: "no-match", sku: "" };
      },
    };
  }, [variationOptions]);

  useEffect(() => () => {
    Object.values(autoMatchTimersRef.current || {}).forEach((timer) => {
      if (timer) window.clearTimeout(timer);
    });
  }, []);

  const triggerSkuAutoMatchFeedback = (rowKey) => {
    if (!rowKey) return;
    const existingTimer = autoMatchTimersRef.current?.[rowKey];
    if (existingTimer) window.clearTimeout(existingTimer);

    setAutoMatchedSkuRows((prev) => ({ ...prev, [rowKey]: true }));
    autoMatchTimersRef.current[rowKey] = window.setTimeout(() => {
      setAutoMatchedSkuRows((prev) => {
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
      delete autoMatchTimersRef.current[rowKey];
    }, 1800);
  };
  // helper: figure out disabled state
  const isDisabled = (row, field) => {
    // Converted rows normally derive Margin, but a Retail-only lock lets the
    // user change Margin by deriving a new supplier Net Cost at the saved rate.
    if (field === "margin" && hasConvertedCostBasis(row) &&
        !(isLockedBool(row?.isRetailLocked) && !isLockedBool(row?.isLocked) && !inventoryMode)) return true;
    // locked behavior (parent enforces too)
    if (lockConfig?.isFieldDisabled) return !!lockConfig.isFieldDisabled(row, field);
    if (respectRowLocks && isLockedBool(row?.isLocked)) return !["netCost", "sku"].includes(field);
    // ⛔️ REMOVED: manual-mode margin lockout; margin is editable in manual now
    return false;
  };

  const netCostHeader = supplierCurrencyCode
    ? `Net Cost (${supplierCurrencyCode})`
    : "Net Cost";

  const normalizedPricingCurrencyCode = String(pricingCurrencyCode || "CAD").toUpperCase();
  const isPromoStorePricingLayout = inventoryMode && !allowManualPricing;
  const promoStoreVisibleTotals = useMemo(() => {
    if (!isPromoStorePricingLayout) return [];
    return computePromoStorePricingBreakdownRows({
      pricingRows: rows,
      decorations,
    });
  }, [decorations, isPromoStorePricingLayout, rows]);
  const promoStoreVisibleTotalsByRowId = useMemo(
    () =>
      new Map(
        promoStoreVisibleTotals
          .filter((result) => result?.rowId != null && result?.rowId !== "")
          .map((result) => [String(result.rowId), result])
      ),
    [promoStoreVisibleTotals]
  );

  const retailHeader = `Retail (${normalizedPricingCurrencyCode})`;
  const clientPriceHeader = `Client Price (${normalizedPricingCurrencyCode})`;
  const totalHeader = `Total (${normalizedPricingCurrencyCode})`;
  const promoStoreRetailHeader = `Retail (${normalizedPricingCurrencyCode})`;
  const promoStoreClientPriceHeader = `Client Price (${normalizedPricingCurrencyCode})`;
  const promoStoreTotalHeader = `Total (${normalizedPricingCurrencyCode})`;

  const inventoryTrackingOptions = [
    { id: INVENTORY_TRACKING.track, name: INVENTORY_TRACKING.track },
    { id: INVENTORY_TRACKING.noTrack, name: INVENTORY_TRACKING.noTrack },
  ];

  const createBreakdownRow = ({
    color = "",
    size = "",
    sku = "",
  } = {}) => ({
    id: "",
    tempRowId: makeTempRowId(),
    priceType: "Breakdown",
    type: "",
    asiId: orderItem?.ProductId || "",
    color,
    size,
    sku,
    quantity: 1,
    inventoryQuantity: null,
    netCost: 0,
    margin: defaultMargin,
    retail: 0,
    clientPrice: 0,
    total: 0,
    inventoryTracking: inventoryMode ? "Track inventory" : undefined,
    isLocked: false,
    isRetailLocked: false,
    isManual: false,
  });

  const toggleBulkNetCostLock = () => {
    if (typeof onToggleBulkNetCostLock === "function") {
      onToggleBulkNetCostLock();
      return;
    }

    const nextLocked = !bulkNetCostLocked;
    setRows(
      (rows || []).map((row) => ({
        ...row,
        isLocked: nextLocked,
      }))
    );
  };


  // unified change handler: validate input locally, then delegate to patchRow
  const handleRowChange = (index, field, rawValue) => {
    const row = rows?.[index] || {};
    const locked = isLockedBool(row?.isLocked);
    const inventoryTrackingValue = resolveInventoryTracking(row?.inventoryTracking);
    const isInventoryLocked = inventoryMode && inventoryTrackingValue === "Do not track inventory";

    if (respectRowLocks && isDisabled(row, field)) return;
    if ((field === "quantity" || field === "inventoryQuantity") && isInventoryLocked) return;

    if (["netCost", "margin", "retail", "clientPrice", "total"].includes(field)) {
      const s = String(rawValue ?? "");
      if (!DECIMAL_RX.test(s)) return;

      // keep in transient form while typing
      if (isTransientDecimal(s)) {
        patchRow(index, { [field]: s, __transientDecimal: true });
      } else {
        const n = Number(s);
        if (!Number.isFinite(n)) return;
        patchRow(index, { [field]: n, __transientDecimal: false });
      }
      return;
    }

    if (field === "quantity" || field === "inventoryQuantity") {
      const s = String(rawValue ?? "");
      if (!INTEGER_RX.test(s)) return;
      patchRow(index, { [field]: s === "" ? "" : parseInt(s, 10) });
      return;
    }

    patchRow(index, { [field]: rawValue });
  };

  const isFixedQuantityType = (row) => ["Fixed Charge", "Inbound freight", "Brokerage", "Duty"].includes(row?.priceType);

  const handleInventoryTrackingChange = (index, event) => {
    patchRow(index, { inventoryTracking: event?.target?.value });
  };

  const allRowsTrackInventory = areAllPricingRowsTrackingInventory(rows);
  const inventoryBulkTooltip = allRowsTrackInventory
    ? "Stop tracking stock for all rows"
    : "Track stock for all rows";

  const handleBulkInventoryTrackingClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setRows((currentRows) => bulkTogglePricingRowsInventoryTracking(currentRows));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      setTimeout(() => {
        if (
          costDropdownRef?.current &&
          !costDropdownRef.current.contains(event.target) &&
          !costDropdownMenuRef?.current?.contains(event.target) &&
          intlCostDropdownRef?.current &&
          !intlCostDropdownRef.current.contains(event.target) &&
          !intlCostDropdownMenuRef?.current?.contains(event.target)
        ) {
          showCostDropdown && setShowCostDropdown(false);
          showInternationalCostDropdown &&
            setInternationalShowCostDropdown(false);
        }
      }, 0);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCostDropdown, showInternationalCostDropdown]);

  const addBreakdownRow = () => {
    setRows((currentRows) => {
      const existingRows = Array.isArray(currentRows) ? currentRows : [];
      // Copy the previous breakdown's editable pricing fields, not database IDs
      // or cart/snapshot relationships. Each new line remains independent.
      const previous = [...existingRows].reverse().find((row) => row?.priceType === "Breakdown");
      const clone = previous ? buildFirstRowClonePatch(previous) : {};
      return [...existingRows, {
        ...createBreakdownRow(),
        ...clone,
        id: "",
        tempRowId: makeTempRowId(),
        priceType: "Breakdown",
      }];
    });
  };

  const addVariationRows = (selectedVariations = []) => {
    const existingBreakdownKeys = new Set(
      (rows || [])
        .filter((row) => row?.priceType === "Breakdown")
        .map((row) => `${normalizeVariationKey(row?.color)}::${normalizeVariationKey(row?.size)}`)
    );

    const queuedKeys = new Set();
    const newVariationRows = [];

    (selectedVariations || []).forEach((variation) => {
      const color = normalizeVariationValue(variation?.color);
      const size = normalizeVariationValue(variation?.size);

      if (!color || !size) return;

      const key = `${normalizeVariationKey(color)}::${normalizeVariationKey(size)}`;

      if (existingBreakdownKeys.has(key) || queuedKeys.has(key)) return;

      queuedKeys.add(key);
      newVariationRows.push(
        createBreakdownRow({
          color,
          size,
          sku: normalizeVariationValue(variation?.sku),
        })
      );
    });

    if (newVariationRows.length === 0) return false;

    setRows(
      [
        ...(rows || []),
        ...newVariationRows,
      ]
    );

    return true;
  };

  const toggleVariationsFlyout = () => {
    const nextOpen = !showVariationsFlyout;
    setShowCostDropdown(false);
    setInternationalShowCostDropdown(false);
    setShowVariationsFlyout(nextOpen);
  };

  const moveRow = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= (rows || []).length) return;

    const nextRows = [...(rows || [])];
    [nextRows[index], nextRows[targetIndex]] = [nextRows[targetIndex], nextRows[index]];
    setRows(nextRows);
  };

  const applyFirstRow = () => {
    if (firstBreakdownIndex < 0 || typeof applyPricingPatch !== "function") return;
    setRows((prevRows = []) => applyFirstRowToUntouchedBreakdownRows(prevRows, {
      defaultMargin,
      applyPricingPatch,
      canApply: (row) =>
        !["quantity", "netCost", "margin", "color", "size", "sku"].some((field) => isDisabled(row, field)),
    }));
  };

  const requestRemoveRow = (row) => {
    setPendingDeleteRowKey(getStableRowKey(row));
  };

  const closeRemoveDialog = () => {
    setPendingDeleteRowKey(null);
  };

  const confirmRemoveRow = () => {
    if (!pendingDeleteRowKey) return;
    setRows((prevRows = []) => prevRows.filter((row) => getStableRowKey(row) !== pendingDeleteRowKey));
    setPendingDeleteRowKey(null);
  };

  const handleOpenSettings = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setSettingsAnchorEl(null);
  };

  const addChargeRow = (type) => {
    const name =
      type === "Inbound freight" || type === "Brokerage" || type === "Duty"
        ? type
        : "";
    setRows(
      [
        ...rows,
        {
          id: "",
          tempRowId: makeTempRowId(),
          priceType: type,
          chargeName: name,
          pricingOption: "Display to Client",
          sku: "", 
          internationalPricingOption: "Display to Client",
          quantity: 1,
          netCost: 0,
          margin: defaultMargin,
          retail: 0,
          clientPrice: 0,
          total: 0,
          inventoryTracking: inventoryMode ? "Track inventory" : undefined,
          isLocked: false,
          isRetailLocked: false,
          isManual: false,
        },
      ]
    );
  };

  const isDutyAlreadyAdded = () => rows.some((row) => row.priceType === "Duty");

  const handleDropdownChange = (index, selections) => {
    const row = rows?.[index] || {};
    const nextColor = normalizeVariationValue(selections?.color);
    const nextSize = normalizeVariationValue(selections?.size);
    const nextPatch = { ...selections };

    if (nextColor && nextSize) {
      const resolved = variationSkuResolver.resolve(nextColor, nextSize);
      if (resolved.status === "match") {
        const matchedSku = normalizeSkuValue(resolved.sku);
        const currentSku = normalizeSkuValue(row?.sku);
        nextPatch.sku = matchedSku;
        if (matchedSku && matchedSku !== currentSku) {
          triggerSkuAutoMatchFeedback(getRowVisualKey(row, index));
        }
      }
    }

    patchRow(index, nextPatch);
  };

  const handlePricingOptionChange = (index, event) => {
    const row = rows?.[index];
    if (!row) return;
    if (isDisabled(row, "pricingOption")) return;
    const nextValue = event?.target?.value ?? event?.value ?? event;
    patchRow(index, {
      pricingOption: normalizeDisplayStatus(nextValue, {
        allowed: DISPLAY_STATUS_OPTIONS.STANDARD.map((opt) => opt.id),
      }),
    });
  };

  const handleInternationalPricingOptionChange = (index, event) => {
    const row = rows?.[index];
    if (!row) return;
    if (isDisabled(row, "internationalPricingOption")) return;
    const nextValue = event?.target?.value ?? event?.value ?? event;
    const normalized = normalizeDisplayStatus(nextValue, {
      allowed: DISPLAY_STATUS_OPTIONS.INTERNATIONAL.map((opt) => opt.id),
      fallback: DISPLAY_STATUS.DISPLAY_TO_CLIENT,
    });
    patchRow(index, {
      internationalPricingOption: normalized,
      pricingOption: normalized,
    });
  };

  const formatRow = (index, field) => {
    const row = rows?.[index];
    if (!row) return;

    const locked = isLockedBool(row?.isLocked);

    const commitNum = (v) => normalizeEditableDecimalForCommit(v);
    const commitNum4 = (v) => commitNum(v);
    const commitInt = (v) => {
      if (v === "") return v;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : v;
    };

    if (locked) {
      // Preserve the existing IsLocked whole-row behavior
      patchRow(index, {
        netCost: commitNum4(row.netCost),
        margin: row.margin,
        retail: row.retail,
        clientPrice: row.clientPrice,
        total: row.total,
        quantity: row.quantity,
      });
      return;
    }

    if (isLockedBool(row.isRetailLocked) && !inventoryMode && field) {
      // Commit only the field that blurred. Sending Margin and Net Cost together
      // would treat a derived Margin as a fresh user edit and reverse the cost.
      const value = ["quantity", "inventoryQuantity"].includes(field)
        ? commitInt(row[field])
        : commitNum4(row[field]);
      patchRow(index, { [field]: value });
      return;
    }

    // Unlocked → commit numerics; parent will recompute per mode
    patchRow(index, {
      netCost: commitNum4(row.netCost),
      margin: commitNum(row.margin),
      retail: commitNum(row.retail),
      clientPrice: commitNum(row.clientPrice),
      total: commitNum(row.total),
      quantity: commitInt(row.quantity),
    });
  };

  return (
    <div>
      <Box className={styles.pricingHeadingRow}>
        <Typography variant="h5" sx={{ mt: 3, mb: 1, fontWeight: "bold" }}>
          Pricing
        </Typography>
        <Tooltip title="Pricing settings">
          <IconButton
            size="small"
            aria-label="Open pricing settings"
            className={styles.pricingSettingsButton}
            onClick={handleOpenSettings}
          >
            <TuneOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <PricingSettingsPopover
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={handleCloseSettings}
        pricingGridMode={pricingGridMode}
        onPricingGridModeChange={onPricingGridModeChange}
        rows={rows}
        pricingCurrencyCode={pricingCurrencyCode}
      />
      <Table className={`${styles.table} popup-edit-pricing-breakdown-table`}>
        <TableHead>
          {isPromoStorePricingLayout ? (
            <TableRow className={styles.tableHeader}>
              <TableCell className={styles.tableHeaderCell}></TableCell>
              <TableCell className={styles.tableHeaderCell}>Color/Size</TableCell>
              <TableCell className={styles.tableHeaderCell}>SKU ID</TableCell>
              <TableCell
                className={`${styles.tableHeaderCell} ${inventoryMode ? styles.inventoryHeaderCell : ""}`}
                data-testid={inventoryMode ? "inventory-header-cell" : undefined}
                onMouseEnter={() => setShowInventoryBulkAction(true)}
                onMouseLeave={() => setShowInventoryBulkAction(false)}
              >
                {inventoryMode ? (
                  <Box className={styles.inventoryHeaderContent}>
                    <span>Stock</span>
                    {showInventoryBulkAction ? (
                      <Tooltip title={inventoryBulkTooltip}>
                        <IconButton
                          size="small"
                          aria-label={inventoryBulkTooltip}
                          className={`${styles.inventoryBulkButton} ${
                            allRowsTrackInventory ? styles.inventoryBulkButtonActive : ""
                          }`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={handleBulkInventoryTrackingClick}
                        >
                          <Inventory2OutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : "Quantity"}
              </TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.promoStoreGroupHeader}`}>
                {showNetCostLocks ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={bulkNetCostLocked ? "Unlock all Net Cost rows" : "Lock all Net Cost rows"}>
                      <IconButton
                        size="small"
                        onClick={toggleBulkNetCostLock}
                        aria-label={bulkNetCostLocked ? "Unlock all Net Cost rows" : "Lock all Net Cost rows"}
                      >
                        {bulkNetCostLocked ? (
                          <LockOutlinedIcon sx={{color: "#FFF"}} fontSize="small" />
                        ) : (
                          <LockOpenOutlinedIcon sx={{color: "#FFF"}} fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <span>{netCostHeader}</span>
                  </Box>
                ) : (
                  netCostHeader
                )}
              </TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.promoStoreGroupHeader}`}>Margin %</TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.promoStoreGroupHeader} ${styles.promoStoreRetailHeaderCell}`}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <span>{promoStoreRetailHeader}</span>
                  <Tooltip title={promoStoreRetailHeaderTooltip}>
                    <InfoOutlinedIcon fontSize="inherit" aria-label="Retail pricing info" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.promoStoreGroupHeader}`}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <span>{promoStoreClientPriceHeader}</span>
                  <Tooltip title={promoStoreClientPriceHeaderTooltip}>
                    <InfoOutlinedIcon fontSize="inherit" aria-label="Client price info" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell className={`${styles.tableHeaderCell} ${styles.promoStoreGroupHeader}`}>{promoStoreTotalHeader}</TableCell>
            </TableRow>
          ) : (
            <TableRow className={styles.tableHeader}>
              <TableCell className={styles.tableHeaderCell}></TableCell>
              <TableCell className={styles.tableHeaderCell}>Color/Size</TableCell>
              <TableCell className={styles.tableHeaderCell}>SKU ID</TableCell>
              <TableCell
                className={`${styles.tableHeaderCell} ${inventoryMode ? styles.inventoryHeaderCell : ""}`}
                data-testid={inventoryMode ? "inventory-header-cell" : undefined}
                onMouseEnter={() => setShowInventoryBulkAction(true)}
                onMouseLeave={() => setShowInventoryBulkAction(false)}
              >
                {inventoryMode ? (
                  <Box className={styles.inventoryHeaderContent}>
                    <span>Stock</span>
                    {showInventoryBulkAction ? (
                      <Tooltip title={inventoryBulkTooltip}>
                        <IconButton
                          size="small"
                          aria-label={inventoryBulkTooltip}
                          className={`${styles.inventoryBulkButton} ${
                            allRowsTrackInventory ? styles.inventoryBulkButtonActive : ""
                          }`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={handleBulkInventoryTrackingClick}
                        >
                          <Inventory2OutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : "Quantity"}
              </TableCell>
              <TableCell className={styles.tableHeaderCell}>
                {showNetCostLocks ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title={bulkNetCostLocked ? "Unlock all Net Cost rows" : "Lock all Net Cost rows"}>
                      <IconButton
                        size="small"
                        onClick={toggleBulkNetCostLock}
                        aria-label={bulkNetCostLocked ? "Unlock all Net Cost rows" : "Lock all Net Cost rows"}
                      >
                        {bulkNetCostLocked ? (
                          <LockOutlinedIcon sx={{color: "#FFF"}} fontSize="small" />
                        ) : (
                          <LockOpenOutlinedIcon sx={{color: "#FFF"}} fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <span>{netCostHeader}</span>
                  </Box>
                ) : (
                  netCostHeader
                )}
              </TableCell>
              <TableCell className={styles.tableHeaderCell}>Margin %</TableCell>
              <TableCell className={styles.tableHeaderCell}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <span>{retailHeader}</span>
                  {showRetailBulkLock && (
                    <Tooltip title={retailBulkLocked ? "Unlock all retail pricing rows" : "Lock all retail pricing rows"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={onToggleRetailBulkLock}
                          disabled={!rows?.length || typeof onToggleRetailBulkLock !== "function"}
                          aria-label={retailBulkLocked ? "Unlock all retail pricing rows" : "Lock all retail pricing rows"}
                          sx={{ color: "inherit", padding: 0.25 }}
                        >
                          {retailBulkLocked ? <LockOutlinedIcon fontSize="small" /> : <LockOpenOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell className={styles.tableHeaderCell}>{clientPriceHeader}</TableCell>
              <TableCell className={styles.tableHeaderCell}>{totalHeader}</TableCell>
            </TableRow>
          )}
        </TableHead>

        <TableBody>
          {rows?.map((row, index) => {
            const locked = isLockedBool(row?.isLocked);
            const inventoryTrackingValue = resolveInventoryTracking(row?.inventoryTracking);
            const isInventoryLocked =
              inventoryMode && inventoryTrackingValue === "Do not track inventory";
            const rowVisualKey = getRowVisualKey(row, index);
            const showSkuAutoMatched = Boolean(autoMatchedSkuRows[rowVisualKey]);
            const promoStoreTotalSnapshot = promoStoreVisibleTotalsByRowId.get(
              getPromoStoreSnapshotRowKey(row)
            );
            const promoStoreVisibleClientPrice =
              promoStoreTotalSnapshot?.effectiveUnitClientPrice ?? row.clientPrice;
            const promoStoreVisibleTotal = promoStoreTotalSnapshot?.clientTotal ?? row.total;
            const promoStoreDecorationCostPerUnit =
              promoStoreTotalSnapshot?.quantity > 0
                ? Number(promoStoreTotalSnapshot?.includedCost || 0) / Number(promoStoreTotalSnapshot.quantity)
                : Number(promoStoreTotalSnapshot?.includedCost || 0);
            const promoStoreEffectiveCostPerUnit =
              promoStoreTotalSnapshot?.quantity > 0
                ? Number(promoStoreTotalSnapshot?.effectiveCost || 0) / Number(promoStoreTotalSnapshot.quantity)
                : Number(promoStoreTotalSnapshot?.effectiveCost || 0);
            return (
              <TableRow key={row.id || row.tempRowId || index} className={styles.row}>
                <TableCell className={styles.iconCell}>
                  <Box className={styles.rowActions}>
                    {index === firstBreakdownIndex && breakdownIndexes.length >= 2 && hasUntouchedBreakdownBelow && typeof applyPricingPatch === "function" && (
                      <Tooltip title="Copy first row to untouched rows below">
                        <IconButton
                          onClick={applyFirstRow}
                          className={styles.actionIcon}
                          size="small"
                          aria-label="Copy first row to untouched rows below"
                        >
                          <AttachMoneyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Move pricing row up">
                      <span>
                        <IconButton
                          onClick={() => moveRow(index, -1)}
                          className={styles.actionIcon}
                          size="small"
                          aria-label="Move pricing row up"
                          disabled={index === 0}
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move pricing row down">
                      <span>
                        <IconButton
                          onClick={() => moveRow(index, 1)}
                          className={styles.actionIcon}
                          size="small"
                          aria-label="Move pricing row down"
                          disabled={index === (rows?.length || 0) - 1}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove pricing row">
                      <span>
                        <IconButton
                          onClick={() => requestRemoveRow(row)}
                          className={`${styles.actionIcon} ${styles.deleteIcon}`}
                          size="small"
                          aria-label="Remove pricing row"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
                {row?.priceType !== "Breakdown" ? (
                  <>
                    <TableCell colSpan={2} className={styles.tableBodyCell}>
                      <Box className="field">
                        <CustomTextField
                          name="chargeName"
                          placeholder={`${row.priceType} Name`}
                          value={row.chargeName}
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "chargeName")}
                        />
                      </Box>
                    </TableCell>

                    <TableCell className={styles.tableBodyCell}>
                      <Box className="field" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <CustomTextField
                          name="quantity"
                          placeholder="Qty"
                          value={
                            isFixedQuantityType(row) ? 1 : row.quantity
                          }
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "quantity") || isInventoryLocked || isFixedQuantityType(row)}
                          handleBlur={() => formatRow(index, "quantity")}
                        />
                        {inventoryMode && (
                          <PricingTypeSelect
                            onChange={(value) => handleInventoryTrackingChange(index, value)}
                            value={inventoryTrackingValue}
                            options={inventoryTrackingOptions}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell className={styles.tableBodyCell}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {showNetCostLocks && (
                          <Tooltip
                            title={
                              locked
                                ? "Locked: only Net Cost editable; Quantity disabled"
                                : "Unlocked"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleLock?.(index)}
                              aria-label="Toggle retail lock"
                              sx={{display: "none"}}
                            >
                              {locked ? (
                                <LockOutlinedIcon fontSize="small" />
                              ) : (
                                <LockOpenOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Net Cost */}
                        <CustomTextField
                          name="netCost"
                          placeholder="Net Cost"
                          pricing
                          value={row.netCost}
                          formatDisplayValue={formatMinimumTwoDecimalPlaces}
                          handleBlur={() => formatRow(index, "netCost")}
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "netCost")}
                          inputProps={{ inputMode: "decimal" }}
                        />
                      </Box>
                    </TableCell>

                    {/* Margin */}
                    <TableCell className={styles.tableBodyCell}>
                      {row.internationalPricingOption !== "Subtract From Margin" && (
                        <CustomTextField
                          name="margin"
                          placeholder={hasConvertedCostBasis(row) && !isLockedBool(row?.isRetailLocked) ? "Derived Margin %" : "Margin %"}
                          value={row.margin}
                          formatDisplayValue={formatMinimumTwoDecimalPlaces}
                          pricing
                          isMarginField
                          handleBlur={() => formatRow(index, "margin")}
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "margin")}
                        />
                      )}
                    </TableCell>

                    {/* Retail */}
                    <TableCell className={styles.tableBodyCell}>
                      {row.internationalPricingOption !== "Subtract From Margin" && (
                        <CustomTextField
                          name="retail"
                          value={row.retail}
                          formatDisplayValue={formatMinimumTwoDecimalPlaces}
                          pricing
                          handleBlur={() => formatRow(index, "retail")}
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "retail")}
                        />
                      )}

                        {canToggleManualPricing ? (
                          <Tooltip
                            title={getManualPricingModeTooltip(row)}
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  (!respectRowLocks || !locked) &&
                                  patchRow(index, { isManual: !row.isManual })
                                }
                                aria-label="Toggle manual pricing"
                                disabled={respectRowLocks && locked}
                              >
                                {row.isManual ? (
                                  <CalculateIcon fontSize="small" />
                                ) : (
                                  <CalculateOutlinedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}

                    </TableCell>

                    {/* Display/Include select */}
                    <TableCell className={styles.tableBodyCell}>
                      {["Inbound freight", "Brokerage", "Duty"].includes(row?.priceType) ? (
                        <PricingTypeSelect
                          disabled={isDisabled(row, "internationalPricingOption")}
                          onChange={(value) =>
                            handleInternationalPricingOptionChange(index, value)
                          }
                          value={row?.internationalPricingOption}
                          options={DISPLAY_STATUS_OPTIONS.INTERNATIONAL}
                        />
                      ) : (
                        <PricingTypeSelect
                          disabled={isDisabled(row, "pricingOption")}
                          onChange={(value) =>
                            handlePricingOptionChange(index, value)
                          }
                          value={row?.pricingOption}
                          options={DISPLAY_STATUS_OPTIONS.STANDARD}
                        />
                      )}
                    </TableCell>

                    {/* Total */}
                    <TableCell className={styles.tableBodyCell}>
                      {row.internationalPricingOption !== "Subtract From Margin" && row.total}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className={styles.tableBodyCell}>
                      <CustomDropdown
                        sizeOptions={toStrings(sizeOptions)}
                        colorOptions={toStrings(colorOptions)}
                        setColorOptions={setColorOptions}
                        setSizeOptions={setSizeOptions}
                        value={
                          row?.color && row?.size ? `${row?.size}/${row?.color}` : ""
                        }
                        color={row?.color}
                        size={row?.size}
                        onSelectionChange={(selections) =>
                          handleDropdownChange(index, selections)
                        }
                      />
                    </TableCell>

                    <TableCell className={styles.tableBodyCell}>
                      <Box className={`field ${showSkuAutoMatched ? styles.skuAutoMatched : ""}`}>
                        <Autocomplete
                          freeSolo
                          options={toStrings(skuOptions)}
                          value={row?.sku || ""}
                          onChange={(_, val) => handleRowChange(index, "sku", val || "")}
                          onInputChange={(_, val) => handleRowChange(index, "sku", val || "")}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select or type SKU"
                              size="small"
                              sx={{minWidth: "140px"}}
                            />
                          )}
                          // optional: ensure dropdown opens on focus
                          selectOnFocus
                          clearOnBlur={false}
                          handleHomeEndKeys
                        />
                      </Box>
                    </TableCell>


                    <TableCell className={styles.tableBodyCell}>
                      <Box className="field" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <CustomTextField
                          name={separateInventoryFromPricing ? "inventoryQuantity" : "quantity"}
                          handleBlur={() => formatRow(index, separateInventoryFromPricing ? "inventoryQuantity" : "quantity")}
                          placeholder={separateInventoryFromPricing ? "Stock Qty" : "Enter Quantity"}
                          value={
                            separateInventoryFromPricing
                              ? (row.inventoryQuantity ?? "")
                              : row.quantity
                          }
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={
                            isDisabled(
                              row,
                              separateInventoryFromPricing ? "inventoryQuantity" : "quantity"
                            ) || isInventoryLocked
                          }
                        />
                        {inventoryMode && (
                          <PricingTypeSelect
                            onChange={(value) => handleInventoryTrackingChange(index, value)}
                            value={inventoryTrackingValue}
                            options={inventoryTrackingOptions}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell className={styles.tableBodyCell}>
                      <Box className={styles.netCostContainer} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {showNetCostLocks && (
                          <Tooltip title={locked ? "Locked: only Net Cost editable; Quantity disabled" : "Unlocked"}>
                            <IconButton
                              size="small"
                              onClick={() => toggleLock?.(index)}
                              aria-label="Toggle retail lock"
                              sx={{display: "none"}}
                            >
                              {locked ? <LockOutlinedIcon fontSize="small" /> : <LockOpenOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}



                        <CustomTextField
                          name="netCost"
                          placeholder="Enter Net Cost"
                          pricing
                          value={row.netCost}
                          formatDisplayValue={formatMinimumTwoDecimalPlaces}
                          handleBlur={() => formatRow(index, "netCost")}
                          handleChange={(e, name) =>
                            handleRowChange(index, name, e.target.value)
                          }
                          disabled={isDisabled(row, "netCost")}
                        />
                      </Box>
                    </TableCell>

                    <TableCell className={styles.tableBodyCell}>
                      <CustomTextField
                        name="margin"
                        placeholder={hasConvertedCostBasis(row) && !isLockedBool(row?.isRetailLocked) ? "Derived Margin %" : "Enter Margin %"}
                        isMarginField
                        value={row.margin}
                        formatDisplayValue={formatMinimumTwoDecimalPlaces}
                        handleBlur={() => formatRow(index, "margin")}
                        pricing
                        handleChange={(e, name) =>
                          handleRowChange(index, name, e.target.value)
                        }
                        disabled={isDisabled(row, "margin")}
                      />
                    </TableCell>

                    {isPromoStorePricingLayout ? (
                      <>
                        <TableCell className={`${styles.tableBodyCell} ${styles.promoStoreRetailCell}`}>
                          <Box className={styles.promoStoreRetailStack}>
                            <Box className={styles.netCostContainer} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <CustomTextField
                                name="retail"
                                value={row.retail}
                                formatDisplayValue={formatMinimumTwoDecimalPlaces}
                                pricing
                                handleBlur={() => formatRow(index, "retail")}
                                handleChange={(e) =>
                                  handleRowChange(index, "retail", e.target.value)
                                }
                                disabled={isDisabled(row, "retail")}
                              />

                              {canToggleManualPricing ? (
                                <Tooltip
                                  title={getManualPricingModeTooltip(row)}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        !locked && patchRow(index, { isManual: !row.isManual })
                                      }
                                      aria-label="Toggle manual pricing"
                                      disabled={locked}
                                    >
                                      {row.isManual ? <CalculateIcon fontSize="small" /> : <CalculateOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell className={`${styles.tableBodyCell} ${styles.promoStoreDerivedCell}`}>
                          <Tooltip
                            title={(
                              <Box>
                                <Typography variant="body2">Client Price includes decoration costs.</Typography>
                                <Typography variant="body2">
                                  Net Cost: {formatPromoStoreMoney(getPromoStoreRowUnitCost(row))}
                                </Typography>
                                <Typography variant="body2">
                                  Decoration Costs: {formatPromoStoreMoney(promoStoreDecorationCostPerUnit)}
                                </Typography>
                                <Typography variant="body2">
                                  Effective Cost: {formatPromoStoreMoney(promoStoreEffectiveCostPerUnit)}
                                </Typography>
                                <Typography variant="body2">
                                  Margin: {formatPromoStorePercent(row?.margin)}
                                </Typography>
                              </Box>
                            )}
                          >
                            <Box className={styles.promoStoreDerivedValue}>
                              <Typography component="span" className={styles.promoStoreDerivedAmount}>
                                ${promoStoreVisibleClientPrice}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={`${styles.tableBodyCell} ${styles.promoStoreDerivedCell}`}>
                          <Tooltip
                            title={(
                              <Box>
                                <Typography variant="body2">Total includes decoration costs and margin.</Typography>
                                <Typography variant="body2">
                                  Net Cost: {formatPromoStoreMoney(getPromoStoreRowUnitCost(row))}
                                </Typography>
                                <Typography variant="body2">
                                  Decoration Costs: {formatPromoStoreMoney(promoStoreDecorationCostPerUnit)}
                                </Typography>
                                <Typography variant="body2">
                                  Effective Cost: {formatPromoStoreMoney(promoStoreEffectiveCostPerUnit)}
                                </Typography>
                                <Typography variant="body2">
                                  Margin: {formatPromoStorePercent(row?.margin)}
                                </Typography>
                                <Typography variant="body2">
                                  Quantity: {Number(row?.quantity) || 0}
                                </Typography>
                              </Box>
                            )}
                          >
                            <Box className={styles.promoStoreDerivedValue}>
                              <Typography component="span" className={styles.promoStoreDerivedAmount}>
                                ${promoStoreVisibleTotal}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className={styles.tableBodyCell}>
                          <Box className={styles.netCostContainer} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

                            <CustomTextField
                              name="retail"
                              value={row.retail}
                              formatDisplayValue={formatMinimumTwoDecimalPlaces}
                              pricing
                              handleBlur={() => formatRow(index, "retail")}
                              handleChange={(e) =>
                                handleRowChange(index, "retail", e.target.value)
                              }
                              disabled={isDisabled(row, "retail")}
                            />

                              {canToggleManualPricing ? (
                                <Tooltip
                                  title={getManualPricingModeTooltip(row)}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        !locked && patchRow(index, { isManual: !row.isManual })
                                      }
                                      aria-label="Toggle manual pricing"
                                      disabled={locked}
                                    >
                                      {row.isManual ? <CalculateIcon fontSize="small" /> : <CalculateOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                          </Box>
                        </TableCell>

                        <TableCell>${row.clientPrice}</TableCell>
                        <TableCell>${row.total}</TableCell>
                      </>
                    )}
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box className={styles.clickableTextContainer} sx={{ mt: 2 }}>
        <Typography className={styles.clickableText} onClick={addBreakdownRow}>
          + Add Breakdown
        </Typography>

        <Box className={styles.relativeWrapper}>
          <Typography
            className={styles.clickableText}
            ref={variationsAnchorRef}
            onClick={toggleVariationsFlyout}
            aria-label="Add variations as breakdown rows"
          >
            + Add Variations
          </Typography>
          <AddVariationsFlyout
            open={showVariationsFlyout}
            anchorEl={variationsAnchorRef.current}
            colorOptions={toStrings(colorOptions)}
            sizeOptions={toStrings(sizeOptions)}
            variationOptions={variationOptions}
            onClose={() => setShowVariationsFlyout(false)}
            onAdd={addVariationRows}
          />
        </Box>

        <Box className={styles.relativeWrapper}>
          <Typography
            className={styles.clickableText}
            ref={costDropdownRef}
            onClick={() => {
              setShowVariationsFlyout(false);
              setInternationalShowCostDropdown(false);
              setShowCostDropdown((prev) => !prev);
            }}
          >
            + Add Cost
          </Typography>
          {showCostDropdown && (
            <Box className={styles.dropdownContainer} ref={costDropdownMenuRef}>
              <Box
                className={styles.dropdownOption}
                onClick={() => { addChargeRow("Run Charge"); setShowCostDropdown(false); }}
              >
                Run Charge
              </Box>
              <Box
                className={styles.dropdownOption}
                onClick={() => { addChargeRow("Fixed Charge"); setShowCostDropdown(false); }}
              >
                Fixed Charge
              </Box>
            </Box>
          )}
        </Box>

        <Box className={styles.relativeWrapper}>
          <Typography
            className={styles.clickableText}
            ref={intlCostDropdownRef}
            onClick={() => {
              setShowVariationsFlyout(false);
              setShowCostDropdown(false);
              setInternationalShowCostDropdown((prev) => !prev);
            }}
          >
            + Add International Cost
          </Typography>
          {showInternationalCostDropdown && (
            <Box className={styles.dropdownContainer} ref={intlCostDropdownMenuRef}>
              <Box
                className={styles.dropdownOption}
                onClick={() => { addChargeRow("Inbound freight"); setInternationalShowCostDropdown(false); }}
              >
                Inbound freight
              </Box>
              <Box
                className={styles.dropdownOption}
                onClick={() => { addChargeRow("Brokerage"); setInternationalShowCostDropdown(false); }}
              >
                Brokerage
              </Box>
              <Box
                className={styles.dropdownOption}
                onClick={() => {
                  if (!isDutyAlreadyAdded()) {
                    addChargeRow("Duty");
                  }
                  setInternationalShowCostDropdown(false);
                }}
              >
                Duty
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Dialog open={Boolean(pendingDeleteRowKey)} onClose={closeRemoveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Remove pricing row?</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this pricing row? This action will remove it from the item.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemoveRow}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PricingBreakDown;
